import type { ReactNode, JSX } from 'react';
import { useSelector } from 'react-redux';
import { selectPermissionsByResource } from '@features/auth/slice';
import type { PermissionRequirement } from '@app/layout/appNav';

interface PermissionGateProps {
  /** Single requirement. Provide this OR `anyOf`. */
  resource?: string;
  action?: string;
  /**
   * Satisfied when the user holds ANY of these (OR). Use it to mirror the
   * nav's `requires` array gates — e.g. a vendor view surface reachable with
   * `vendor:search` OR `vendor:create`. Takes precedence over resource/action.
   */
  anyOf?: PermissionRequirement[];
  /** Rendered when the user has permission. */
  children: ReactNode;
  /** Rendered when the user lacks permission. Defaults to `null` (hide). */
  fallback?: ReactNode;
}

/**
 * Renders `children` only if the current user satisfies the requirement(s).
 * Use it to gate routes and to hide privileged buttons/menu items.
 * NOTE: backend authorization is still authoritative; this is a UX guardrail.
 */
export function PermissionGate({
  resource,
  action,
  anyOf,
  children,
  fallback = null,
}: PermissionGateProps): JSX.Element {
  const index = useSelector(selectPermissionsByResource);
  const requirements: PermissionRequirement[] =
    anyOf ?? (resource && action ? [{ resource, action }] : []);
  const allowed = requirements.some((r) => index[r.resource]?.has(r.action) ?? false);
  return <>{allowed ? children : fallback}</>;
}
